import React, { useEffect, useState } from 'react'
import {
  GlobalTable,
  HeaderLine,
  Td,
  TdButtons,
  Th,
  Tr,
  FooterTr,
  ConfigButton,
  StickyGearTh,
} from '../../../../components/styles/table-style'
import axios from '../../../../config/axios'
import lookup from '../../../../assets/certificates/Tables/lookup.svg'
import { useNavigate, useOutletContext } from 'react-router-dom'
import PricesDisplay from '../../../../components/PricesDisplay'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import FieldsManager, { FieldsManagerType, WhichFieldsToShowType } from '../../../../components/FieldManager'
import { tableFields } from '../../../../components/FieldManager/table-fields'
import { Settings } from 'lucide-react'
import { useTenant } from '../../../../contexts/TenantContext'

interface PriceItem {
  id: number
  price: string
  unitOfMeasurement: { id: number; name: string }
}

interface ContextType {
  search: string;
}

interface Resource {
  id: number
  name: string
  code?: string
  category: { id: number; name: string } | null
  type: { id: number; name: string } | null
  prices?: PriceItem[]
  description: string
  totalWeight?: number
  lengthInMm?: number
  unitQuantity?: number
  stockUnit?: string
  dimension1?: number | null
  dimension2?: number | null
  material?: string | null
  format?: string | null
  fabrication?: string | null
  ncm?: string | null
  shelf?: string | null
  stockLocation?: string | null
  stockStreet?: string | null
  suppliers: { id: number; companyName: string; cnpj: string }[]
  color?: string
}

const STORAGE_KEY_STEEL   = 'resourceTableFields'
const STORAGE_KEY_FASHION = 'resourceTableFieldsFashion'

const getStoredFields = (isFashion: boolean): FieldsManagerType => {
  const key = isFashion ? STORAGE_KEY_FASHION : STORAGE_KEY_STEEL
  const base = (
    tableFields.find(t => t.tableSection === 'resource') ||
    { tableSection: 'resource', whichFieldsToShow: [] }
  ) as FieldsManagerType

  try {
    const raw = localStorage.getItem(key)
    if (raw) {
      const parsed: FieldsManagerType = JSON.parse(raw)
      if (parsed?.whichFieldsToShow?.length) {
        const parsedMap = new Map(parsed.whichFieldsToShow.map(f => [f.field, f]))
        const merged = base.whichFieldsToShow.map(baseField => {
          const storedField = parsedMap.get(baseField.field)
          return storedField
            ? { ...baseField, checked: storedField.checked }
            : baseField
        })
        return {
          ...base,
          whichFieldsToShow: merged,
        }
      }
    }
  } catch { /* */ }

  if (!isFashion) return base

  return {
    ...base,
    whichFieldsToShow: base.whichFieldsToShow.map(f =>
      f.field === 'name' ? { ...f, fieldName: 'Aviamento' } : f
    ),
  }
}

const ResourcesTable: React.FC = () => {
  const { isFashionTenant } = useTenant()
  const storageKey = isFashionTenant ? STORAGE_KEY_FASHION : STORAGE_KEY_STEEL
  const [resources, setResources] = useState<Resource[]>([])
  const [total, setTotal] = useState<number>(0)
  const [skip, setSkip] = useState<number>(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [fields, setFields] = useState<FieldsManagerType>(() => getStoredFields(isFashionTenant))
  const navigate = useNavigate()
  const TAKE = 10

  const context = useOutletContext<ContextType>() || { search: '' };
const { search } = context;
console.log('📥 [RESOURCES-TABLE] Contexto recebido:', { search });
  
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(handler)
  }, [search])

  

  useEffect(() => {
    const getResources = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`, {
          params: {search: debouncedSearch, skip: 0, take: TAKE}
        })
        if (response.data) {
          setResources(response.data);
        }
      } catch (error) {
        console.error("Erro ao buscar recursos:", error);
      }
    }
    getResources()
  }, [debouncedSearch, ])

  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/resources/resource`,
        { params: { skip, take: TAKE } },
      )
      if (response.data) {
        const newItems: Resource[] = response.data.data ?? response.data
        setResources(prev => [...prev, ...newItems])
        setTotal(response.data.total ?? resources.length + newItems.length)
        setSkip(prev => prev + TAKE)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const getCellValue = (resource: Resource, field: WhichFieldsToShowType): React.ReactNode => {
    switch (field.field) {
      case 'name':        return resource.name
      case 'category':   return resource.category?.name || '—'
      case 'type':       return resource.type?.name || '—'
      case 'code':       return resource.code || '—'
      case 'prices':     return <PricesDisplay prices={(resource.prices || []) as any} maxWidth={200} />
      case 'color':      return (resource as any).color || '—'
      case 'suppliers':  return resource.suppliers?.map(s => s.companyName).join(', ') || '—'
      case 'description':return resource.description || '—'
      case 'totalWeight':  return resource.totalWeight != null ? resource.totalWeight : '—'
      case 'lengthInMm':   return resource.lengthInMm != null ? resource.lengthInMm : '—'
      case 'unitQuantity': return resource.unitQuantity != null ? resource.unitQuantity : '—'
      case 'stockUnit':    return resource.stockUnit || '—'
      case 'dimension1':   return resource.dimension1 != null ? resource.dimension1 : '—'
      case 'dimension2':   return resource.dimension2 != null ? resource.dimension2 : '—'
      case 'material':     return resource.material || '—'
      case 'format':       return resource.format || '—'
      case 'fabrication':  return resource.fabrication || '—'
      case 'ncm':          return resource.ncm || '—'
      case 'shelf':        return resource.shelf || '—'
      case 'stockLocation':return resource.stockLocation || '—'
      case 'stockStreet':  return resource.stockStreet || '—'
      default:             return '—'
    }
  }

  const visibleFields = fields.whichFieldsToShow.filter(f => f.checked)


  const getFieldLabel = (field: WhichFieldsToShowType): string => {
    if (field.field === 'name') return isFashionTenant ? 'Aviamento' : 'Insumo'
    return field.fieldName
  }

  return (
    <>
      {isDropdownOpen && (
        <FieldsManager
          dropdownOpen={setIsDropdownOpen}
          fields={fields}
          setFields={setFields}
        />
      )}
      <GlobalTable>
        <thead>
          <HeaderLine>
            {visibleFields.map((field, index) => (
              <Th key={index}>{getFieldLabel(field)}</Th>
            ))}
            <Th />
            <StickyGearTh>
              <ConfigButton onClick={() => setIsDropdownOpen(prev => !prev)}>
                <Settings size={18} />
              </ConfigButton>
            </StickyGearTh>
          </HeaderLine>
        </thead>
        <tbody>
          {resources.map((resource, index) => (
            <Tr key={index}>
              {visibleFields.map((field, fi) => (
                <Td key={fi}>{getCellValue(resource, field)}</Td>
              ))}
              <TdButtons>
                <img
                  src={lookup}
                  alt='lookup'
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`view?id=${resource.id}`)}
                />
              </TdButtons>
            </Tr>
          ))}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td
              colSpan={visibleFields.length + 2}
              style={{ textAlign: 'center', background: 'transparent', padding: '1rem 0' }}
            >
              <LoadMoreButton
                onLoadMore={handleLoadMore}
                hasMore={resources.length < total}
                currentCount={resources.length}
                totalCount={total}
                isLoading={isLoadingMore}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>
    </>
  )
}

export default ResourcesTable
